import { CircleNotchIcon, TrashIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useApiMutation } from '@/modules/common';

interface ProductSetRemoveButtonProps {
  eventId: string;
  stationId: string;
  productSetId: string;
  productSetName: string;
  onSuccess: () => void;
}

export function ProductSetRemoveButton({
  eventId,
  stationId,
  productSetId,
  productSetName,
  onSuccess,
}: ProductSetRemoveButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const removeProductSetMutation = useApiMutation<void>(
    `/events/${eventId}/stations/${stationId}/product-sets/${productSetId}`,
    {
      method: 'DELETE',
    },
    {
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess,
    }
  );

  const submitRemoveProductSet = async () => {
    try {
      await removeProductSetMutation.mutateAsync();
      toast.success(`Product set “${productSetName}” removed successfully!`);
      setIsOpen(false);
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        type="button"
        data-testid="remove-product-set-button"
        onClick={() => setIsOpen(true)}
      >
        <TrashIcon size={28} color={colors.zinc[400]} />
      </Button>

      <AlertDialog open={isOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the product set “{productSetName}
              ”?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={submitRemoveProductSet}
              disabled={removeProductSetMutation.isPending}
              className="text-destructive border-destructive hover:text-destructive"
              data-testid="remove-product-set-dialog-button"
            >
              {removeProductSetMutation.isPending && (
                <CircleNotchIcon className="animate-spin" />
              )}
              Delete
            </AlertDialogAction>
            <AlertDialogCancel
              disabled={removeProductSetMutation.isPending}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
